import { defaultSnapshot, generatedShell } from './demo-data'
import { hasSupabaseConfig, SupabaseAdapter } from './supabase-adapter'
import type { AppVersion, AppyCreation, AppyDataAdapter, AppySnapshot, DailySchedule, GenerationRequest, GenerationResult, ProfileUpdate, VersionRestoreResult } from './model'

const STORAGE_KEY = 'appy.snapshot.v1'
const RUNTIME_KEY_PATTERN = /^[a-zA-Z0-9_.:-]{1,120}$/

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))

const copySnapshot = (snapshot: AppySnapshot): AppySnapshot => JSON.parse(JSON.stringify(snapshot)) as AppySnapshot
const normalizeSnapshot = (snapshot: Partial<AppySnapshot>): AppySnapshot => ({
  ...copySnapshot(defaultSnapshot),
  ...snapshot,
  settings: { ...defaultSnapshot.settings, ...snapshot.settings },
  billing: { ...defaultSnapshot.billing, ...snapshot.billing },
  account: { ...defaultSnapshot.account, ...snapshot.account },
})

export class LocalDemoAdapter implements AppyDataAdapter {
  readonly mode = 'demo' as const
  private latestSnapshot: AppySnapshot = copySnapshot(defaultSnapshot)

  async loadSnapshot(): Promise<AppySnapshot | null> {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY)
      this.latestSnapshot = saved ? normalizeSnapshot(JSON.parse(saved) as Partial<AppySnapshot>) : copySnapshot(defaultSnapshot)
    } catch {
      this.latestSnapshot = copySnapshot(defaultSnapshot)
    }
    return copySnapshot(this.latestSnapshot)
  }

  async saveSnapshot(snapshot: AppySnapshot): Promise<void> {
    this.latestSnapshot = copySnapshot(snapshot)
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
    } catch {
      // The in-memory store stays usable when storage is unavailable.
    }
  }

  async updateProfile(profile: ProfileUpdate): Promise<void> {
    const me = this.latestSnapshot.users.find((user) => user.id === this.latestSnapshot.currentUserId)
    if (!me) return
    Object.assign(me, profile, { avatar: profile.name.trim().split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || me.avatar })
  }

  async setProfileVisibility(visibility: AppySnapshot['settings']['profileVisibility']): Promise<void> {
    this.latestSnapshot.settings.profileVisibility = visibility
  }

  async setDailySchedule(schedule: DailySchedule): Promise<void> {
    this.latestSnapshot.settings.dailyGame = schedule.enabled
    this.latestSnapshot.settings.dailyGameHour = schedule.hour
    this.latestSnapshot.settings.dailyGameIntelligence = schedule.intelligence
  }

  async openBillingPortal(): Promise<string | null> {
    throw new Error('Stripe billing requires a live Supabase connection with Edge Functions configured.')
  }

  async purchasePlan(): Promise<string | null> {
    throw new Error('Stripe checkout requires a live Supabase connection with Edge Functions configured.')
  }

  async purchaseCredits(): Promise<string | null> {
    throw new Error('Stripe checkout requires a live Supabase connection with Edge Functions configured.')
  }

  async secureAccount(): Promise<{ email: string; verificationRequired: boolean }> {
    throw new Error('Local demo data stays only in this browser. Connect Appy to Supabase to create an account.')
  }

  async signUp(): Promise<{ email: string; verificationRequired: boolean }> {
    throw new Error('Account creation is available when Appy is connected to Supabase.')
  }

  async signIn(): Promise<{ mfaRequired: boolean }> {
    throw new Error('Sign-in is available when Appy is connected to Supabase.')
  }

  async signOut(): Promise<void> {
    // Local demo has no remote session.
  }

  async toggleLike(): Promise<void> {}
  async toggleSave(): Promise<void> {}
  async toggleRepost(): Promise<void> {}
  async toggleFollow(): Promise<void> {}
  async addComment(): Promise<void> {}
  async toggleCommentLike(): Promise<void> {}
  async toggleCommentRepost(): Promise<void> {}
  async recordPlay(): Promise<void> {}

  async generate(request: GenerationRequest): Promise<GenerationResult> {
    request.onProgress?.({ message: 'Planning layout and interactions…', status: 'queued' })
    await wait(400)
    request.onProgress?.({ message: 'Writing HTML, CSS, and JavaScript…', status: 'in_progress' })
    await wait(450)
    const short = request.prompt.trim().split(/[.!?]/)[0]?.slice(0, 44) || 'Untitled creation'
    return {
      title: request.title?.trim() || short.replace(/^(make|build|create)\s+(me\s+)?(an?\s+)?/i, '') || 'New App',
      description: `A ${request.kind} generated from your idea, ready to play, edit, and share.`,
      srcDoc: generatedShell(request.prompt, request.kind),
      tags: [request.kind === 'game' ? 'Game' : 'Utility', 'Made with Appy'],
      estimatedCost: request.intelligence === 'low' ? .18 : request.intelligence === 'high' ? .74 : .39,
    }
  }

  async cancelBuild(): Promise<number> { return 0 }
  async revise(app: AppyCreation, instruction: string, intelligence: GenerationRequest['intelligence'], onProgress?: GenerationRequest['onProgress']): Promise<GenerationResult> {
    onProgress?.({ message: 'Applying your change request…', status: 'in_progress' })
    await wait(700)
    return {
      title: app.title,
      description: app.description,
      srcDoc: app.srcDoc.replace('</body>', `<div style="position:fixed;right:10px;bottom:10px;background:#111;color:#fff;padding:7px 10px;border-radius:99px;font:11px system-ui">Updated: ${instruction.replace(/[<>]/g, '').slice(0, 34)}</div></body>`),
      tags: app.tags,
      estimatedCost: intelligence === 'low' ? .08 : intelligence === 'high' ? .36 : .19,
    }
  }

  async publish(_app: AppyCreation, _body?: string): Promise<string | null> { return crypto.randomUUID() }
  async deletePost(): Promise<void> {}
  async updatePost(): Promise<void> {}
  async createTextPost(body: string, mediaUrl?: string | null) {
    const now = new Date().toISOString()
    const id = crypto.randomUUID()
    return {
      id, postId: id, postType: 'text' as const, authorId: 'me', title: 'Post', description: body, postBody: body,
      prompt: '', kind: 'app' as const, status: 'published' as const, createdAt: now, updatedAt: now, publishedAt: now,
      srcDoc: '', coverClass: 'cover-text', mediaUrl: mediaUrl ?? null, mediaKind: mediaUrl ? 'image' as const : 'none' as const,
      tags: ['Post'], likes: 0, comments: 0, reposts: 0, saves: 0, plays: 0, version: 1, versions: [],
    }
  }
  async createShortPost(body: string, videoUrl: string, durationSeconds?: number | null) {
    const now = new Date().toISOString()
    const id = crypto.randomUUID()
    const caption = body.trim() || 'Short'
    return {
      id, postId: id, postType: 'short' as const, authorId: 'me', title: 'Short', description: caption, postBody: caption,
      prompt: '', kind: 'app' as const, status: 'published' as const, createdAt: now, updatedAt: now, publishedAt: now,
      srcDoc: '', coverClass: 'cover-short', mediaUrl: null, videoUrl, mediaKind: 'video' as const, durationSeconds: durationSeconds ?? null,
      tags: ['Short'], likes: 0, comments: 0, reposts: 0, saves: 0, plays: 0, version: 1, versions: [],
    }
  }
  async setProjectCover(): Promise<void> {}
  async uploadMedia(): Promise<string> { throw new Error('Media uploads require Supabase.') }
  async moderatePostImage(): Promise<void> {}
  async moderatePostVideo(): Promise<void> {}
  async resetPassword(): Promise<void> { throw new Error('Password reset requires Supabase.') }
  async updatePassword(): Promise<void> { throw new Error('Password updates require Supabase.') }
  async listMfaFactors() { return [] as { id: string; friendlyName?: string; status: string }[] }
  async enrollMfa(): Promise<{ id: string; qrCode: string; secret: string; uri: string }> { throw new Error('Two-step authentication requires Supabase.') }
  async verifyMfaEnrollment(): Promise<void> { throw new Error('Two-step authentication requires Supabase.') }
  async unenrollMfa(): Promise<void> { throw new Error('Two-step authentication requires Supabase.') }
  async mfaLoginRequired(): Promise<boolean> { return false }
  async verifyMfaLogin(): Promise<void> { throw new Error('Two-step authentication requires Supabase.') }

  async restoreVersion(app: AppyCreation, version: AppVersion): Promise<VersionRestoreResult> {
    const number = Math.max(0, ...app.versions.map((item) => item.number)) + 1
    const current: AppVersion = {
      ...version,
      id: undefined,
      number,
      note: `Restored from version ${version.number}`,
      createdAt: new Date().toISOString(),
    }
    return {
      current,
      versions: [...app.versions, current],
      status: app.status === 'published' ? 'published' : 'ready',
    }
  }

  private runtimeKey(projectId: string, key: string) {
    if (!RUNTIME_KEY_PATTERN.test(key)) throw new Error('Invalid Appy storage key')
    return `appy.runtime.${projectId}.${key}`
  }

  async runtimeGet(projectId: string, key: string): Promise<unknown> {
    const raw = window.localStorage.getItem(this.runtimeKey(projectId, key))
    if (raw === null) return null
    try { return JSON.parse(raw) as unknown } catch { return raw }
  }

  async runtimeSet(projectId: string, key: string, value: unknown): Promise<void> {
    const serialized = JSON.stringify(value)
    if (serialized === undefined) throw new Error('Appy storage values must be valid JSON')
    window.localStorage.setItem(this.runtimeKey(projectId, key), serialized)
  }

  async runtimeDelete(projectId: string, key: string): Promise<void> {
    window.localStorage.removeItem(this.runtimeKey(projectId, key))
  }

  async runtimePublicProfile(): Promise<unknown> {
    const user = this.latestSnapshot.users.find((item) => item.id === this.latestSnapshot.currentUserId)
    if (!user || this.latestSnapshot.settings.profileVisibility !== 'public') return null
    return { id: user.id, handle: user.handle, display_name: user.name, avatar_url: null, bio: user.bio, is_verified: Boolean(user.verified) }
  }
}

export const appyAdapter: AppyDataAdapter = hasSupabaseConfig && import.meta.env.VITE_DEMO_MODE !== 'true'
  ? new SupabaseAdapter()
  : new LocalDemoAdapter()
